import { useLayoutEffect } from 'react';

type UseStatusBarAppearanceOptions = {
  themeColor?: string;
  topInsetColor?: string;
  appleStatusBarStyle?: 'default' | 'black' | 'black-translucent';
  rootSafeAreaTop?: string;
};

/**
 * Controls mobile/PWA status bar appearance by route.
 * - themeColor updates browser chrome color on supported platforms.
 * - topInsetColor paints the iOS safe-area strip in standalone mode.
 */
export function useStatusBarAppearance({
  themeColor,
  topInsetColor,
  appleStatusBarStyle,
  rootSafeAreaTop,
}: UseStatusBarAppearanceOptions): void {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const appleMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');

    const previousThemeColor = themeMeta?.getAttribute('content') ?? null;
    const previousAppleStyle = appleMeta?.getAttribute('content') ?? null;
    const previousTopInsetColor = root.style.getPropertyValue('--status-bar-top-bg');
    const previousRootSafeAreaTop = root.style.getPropertyValue('--app-root-safe-area-top');

    if (themeMeta && themeColor) {
      themeMeta.setAttribute('content', themeColor);
    }

    if (appleMeta && appleStatusBarStyle) {
      appleMeta.setAttribute('content', appleStatusBarStyle);
    }

    if (topInsetColor) {
      root.style.setProperty('--status-bar-top-bg', topInsetColor);
    }

    if (rootSafeAreaTop != null) {
      root.style.setProperty('--app-root-safe-area-top', rootSafeAreaTop);
    }

    return () => {
      if (themeMeta) {
        if (previousThemeColor != null) {
          themeMeta.setAttribute('content', previousThemeColor);
        } else {
          themeMeta.removeAttribute('content');
        }
      }

      if (appleMeta) {
        if (previousAppleStyle != null) {
          appleMeta.setAttribute('content', previousAppleStyle);
        } else {
          appleMeta.removeAttribute('content');
        }
      }

      if (previousTopInsetColor) {
        root.style.setProperty('--status-bar-top-bg', previousTopInsetColor);
      } else {
        root.style.removeProperty('--status-bar-top-bg');
      }

      if (previousRootSafeAreaTop) {
        root.style.setProperty('--app-root-safe-area-top', previousRootSafeAreaTop);
      } else {
        root.style.removeProperty('--app-root-safe-area-top');
      }
    };
  }, [themeColor, topInsetColor, appleStatusBarStyle, rootSafeAreaTop]);
}
